import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Trash2, Clock, CheckCircle2, 
  Calendar, User, X, Filter, ListTodo, Check
} from 'lucide-react';
import { taskService, userService } from '../services/api';

interface Task {
  id: number;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'completed';
  due_date: string | null;
  created_by: number;
  assigned_to: number | null;
  completed_at: string | null;
  created_at: string;
  creator_first_name: string;
  creator_last_name: string;
  creator_initials: string;
  assignee_first_name: string | null;
  assignee_last_name: string | null;
  assignee_initials: string | null;
}

interface AdminUser {
  id: number;
  first_name: string;
  last_name: string;
  initials: string;
  email: string;
}

const priorityConfig = {
  urgent: { label: 'Urgente', color: 'text-red-600', bg: 'bg-red-100', dot: 'bg-red-500' },
  high: { label: 'Alta', color: 'text-orange-600', bg: 'bg-orange-100', dot: 'bg-orange-500' },
  medium: { label: 'Media', color: 'text-blue-600', bg: 'bg-blue-100', dot: 'bg-blue-500' },
  low: { label: 'Baja', color: 'text-gray-600', bg: 'bg-gray-100', dot: 'bg-gray-400' },
};

const AdminTasks: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pending' | 'completed'>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assigned_to: '',
  });

  useEffect(() => {
    fetchTasks();
    fetchAdmins();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await taskService.getAll();
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await userService.getAll();
      const adminUsers = response.data.filter((u: any) => u.role === 'admin');
      setAdmins(adminUsers);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        title: formData.title,
        description: formData.description || undefined,
        priority: formData.priority,
        due_date: formData.due_date || undefined,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : undefined,
      };

      if (editingTask) {
        await taskService.update(editingTask.id, data);
      } else {
        await taskService.create(data);
      }
      
      fetchTasks();
      closeModal();
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleToggleStatus = async (task: Task) => {
    try {
      const newStatus = task.status === 'pending' ? 'completed' : 'pending';
      await taskService.update(task.id, { status: newStatus });
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      await taskService.delete(id);
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const openModal = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description || '',
        priority: task.priority,
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        assigned_to: task.assigned_to?.toString() || '',
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        due_date: '',
        assigned_to: '',
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTask(null);
  };

  const isOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date(new Date().toDateString());
  };

  const filteredTasks = tasks.filter(task => {
    const matchesTab = task.status === activeTab;
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    return matchesTab && matchesSearch && matchesPriority;
  });

  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <Clock size={22} className="text-blue-600" />
              <h1 className="text-xl font-bold text-gray-800">Recordatorios</h1>
            </div>
            <p className="text-gray-500 text-sm mt-1">Gestiona <span className="text-blue-600">tareas importantes</span> del equipo</p>
          </div>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            <Plus size={18} />
            <span>New</span>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search reminders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white"
            />
          </div>
          <div className="relative">
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            >
              <option value="all">All priorities</option>
              <option value="urgent">Urgente</option>
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Baja</option>
            </select>
            <Filter className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-medium text-sm transition-all border-b-2 ${
            activeTab === 'pending'
              ? 'text-amber-600 border-amber-500'
              : 'text-gray-400 border-transparent hover:text-gray-600'
          }`}
        >
          <Clock size={16} />
          Pending ({pendingCount})
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-medium text-sm transition-all border-b-2 ${
            activeTab === 'completed'
              ? 'text-emerald-600 border-emerald-500'
              : 'text-gray-400 border-transparent hover:text-gray-600'
          }`}
        >
          <CheckCircle2 size={16} />
          Completed ({completedCount})
        </button>
      </div>

        {/* Task List */}
        <div className="p-4 space-y-3">
          {filteredTasks.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ListTodo size={28} className="text-gray-400" />
              </div>
              <p className="text-gray-500">No hay tareas {activeTab === 'pending' ? 'pendientes' : 'completadas'}</p>
              {activeTab === 'pending' && (
                <button 
                  onClick={() => openModal()}
                  className="mt-3 text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  + Crear primera tarea
                </button>
              )}
            </div>
          ) : (
            filteredTasks.map((task) => (
              <div
                key={task.id}
                className={`group p-4 rounded-xl border transition-all hover:shadow-md ${
                  task.status === 'pending' && isOverdue(task.due_date) 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-white border-gray-100 hover:border-blue-200'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <button
                    onClick={() => handleToggleStatus(task)}
                    className={`mt-0.5 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                      task.status === 'completed'
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    {task.status === 'completed' && (
                      <Check size={14} className="text-white" strokeWidth={3} />
                    )}
                  </button>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => openModal(task)}
                        className={`font-semibold text-left hover:text-blue-600 transition-colors ${
                          task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800'
                        }`}
                      >
                        {task.title}
                      </button>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${priorityConfig[task.priority].bg} ${priorityConfig[task.priority].color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${priorityConfig[task.priority].dot}`}></span>
                        {priorityConfig[task.priority].label}
                      </span>
                      {task.status === 'pending' && isOverdue(task.due_date) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500 text-white">
                          Vencida
                        </span>
                      )}
                    </div>
                    
                    {task.description && (
                      <p className={`text-sm mt-1.5 ${task.status === 'completed' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {task.description}
                      </p>
                    )}

                    <div className="flex items-center gap-4 mt-3 text-xs">
                      {task.due_date && (
                        <span className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${
                          task.status === 'pending' && isOverdue(task.due_date) 
                            ? 'bg-red-100 text-red-700 font-medium' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Calendar size={12} />
                          {new Date(task.due_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {task.assignee_initials && (
                        <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-50 text-blue-700">
                          <User size={12} />
                          {task.assignee_first_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5 text-gray-500">
                        <span className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-[10px] font-bold text-gray-600">
                          {task.creator_initials}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Delete */}
                  <button
                    onClick={() => handleDelete(task.id)}
                    className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  {editingTask ? 'Editar Tarea' : 'Nueva Tarea'}
                </h2>
                <button onClick={closeModal} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white/80 hover:text-white">
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="¿Qué necesitas recordar?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  rows={3}
                  placeholder="Agrega detalles adicionales..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="low">🟢 Baja</option>
                    <option value="medium">🔵 Media</option>
                    <option value="high">🟠 Alta</option>
                    <option value="urgent">🔴 Urgente</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha límite</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Asignar a</label>
                <select
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                >
                  <option value="">Sin asignar</option>
                  {admins.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.first_name} {admin.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm"
                >
                  {editingTask ? 'Guardar Cambios' : 'Crear Tarea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTasks;
